import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, KeyRound, RefreshCw, ShieldOff } from "lucide-react";
import {
  ApiError,
  getAdminTeams,
  getAdminLiveConfig,
  getAdminLiveDiagnostics,
  getTeamStreamKeyAudit,
  killAdminStream,
  rotateTeamStreamKey,
  unblockAdminStream,
  updateAdminSettings,
} from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/components/ui/toast";
import { unscopedKey } from "@/lib/queryClient";
import type {
  AdminLiveConfig,
  AdminLiveDiagnostics,
  AdminDiagnostics,
  AdminPerformance,
  AdminStreamBlock,
  AdminStreamSession,
  AdminTeamRow,
} from "@/lib/types";
import {
  useAdminDiagnostics,
  useAdminPerformance,
  useAdminStreams,
} from "../hooks/useAdminData";
import { AdminIconButton } from "../components/AdminIconButton";
import { AdminPageHeader } from "../components/AdminPageHeader";

/**
 * M8a C1–C7 — `/admin/live` broadcast cockpit.
 *
 * Mounts the 5 s `useAdminPerformance` (encoder telemetry +
 * throughput) AND swaps the shell's 10 s `useAdminStreams` to 5 s
 * cadence while on this route. The shared cache key means both
 * intervals can coexist — last one wins per query.
 *
 * Layout:
 *   - Left rail: ingest config card (RTMP URL + masked stream key +
 *     rotate-key + offline message + master enable toggle)
 *   - Right rail: ON-AIR pill, encoder load tile, live throughput
 *     sparkline, viewers list (with Kill per row), blocks list,
 *     diagnostics block (MediaMTX + live activity)
 *     (with Unblock per row)
 */
export function AdminLivePage() {
  // Re-mount the streams query at 5 s for this route (C7).
  const streamsQuery = useAdminStreams({ refetchIntervalMs: 5_000 });
  const perfQuery = useAdminPerformance({ refetchIntervalMs: 5_000 });
  const diagnosticsQuery = useAdminDiagnostics({ refetchIntervalMs: 10_000 });
  const liveDiagnosticsQuery = useQuery({
    queryKey: unscopedKey("admin-live-diagnostics"),
    queryFn: getAdminLiveDiagnostics,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });
  const configQuery = useQuery({
    queryKey: unscopedKey("admin-live-config"),
    queryFn: getAdminLiveConfig,
  });
  const teamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams-live"),
    queryFn: getAdminTeams,
    staleTime: 60_000,
  });

  const liveSessions =
    streamsQuery.data?.active?.filter((s) => s.kind === "live") ?? [];
  const blocks = streamsQuery.data?.blocks ?? [];
  const liveEvents = liveActivityEvents(diagnosticsQuery.data);
  const keyedTeams = (teamsQuery.data ?? []).filter(
    (team) => Boolean(team.has_stream_key),
  ).length;

  return (
    <div data-testid="admin-live" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Operations"
        title="Live Broadcast"
        description="Prepare team streams for matchday, watch active viewers, and keep ingest issues out of the family experience."
        meta={
          perfQuery.isFetching || streamsQuery.isFetching
            ? "Refreshing..."
            : "Auto-refreshes every 5 s"
        }
      />

      <BroadcastStatusStrip
        config={configQuery.data}
        sessions={liveSessions}
        blocks={blocks}
        liveDiagnostics={liveDiagnosticsQuery.data}
        liveEvents={liveEvents}
        teamCount={teamsQuery.data?.length ?? 0}
        keyedTeams={keyedTeams}
        throughputBps={Number(perfQuery.data?.throughput?.last?.bps_live_out ?? 0)}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,360px)_1fr] gap-3">
        <div className="flex min-w-0 flex-col gap-3">
          <AdminTeamsLiveStrip query={teamsQuery} />
          <IngestConfigCard query={configQuery} />
        </div>
        <div className="flex flex-col gap-3 min-w-0">
          <OnAirAndThroughputRow
            sessions={liveSessions}
            perf={perfQuery.data}
            configEnabled={!!configQuery.data?.enabled}
          />
          <EncoderLoadTile perf={perfQuery.data} />
          <ViewersList sessions={liveSessions} />
          <BlocksList blocks={blocks} />
          <LiveDiagnosticsBlock
            diagnostics={diagnosticsQuery.data}
            liveDiagnostics={liveDiagnosticsQuery.data}
            liveDiagnosticsFetching={liveDiagnosticsQuery.isFetching}
            liveDiagnosticsError={liveDiagnosticsQuery.error}
            onRefresh={() => void liveDiagnosticsQuery.refetch()}
          />
        </div>
      </div>
    </div>
  );
}

function BroadcastStatusStrip({
  config,
  sessions,
  blocks,
  liveDiagnostics,
  liveEvents,
  teamCount,
  keyedTeams,
  throughputBps,
}: {
  config: AdminLiveConfig | undefined;
  sessions: AdminStreamSession[];
  blocks: AdminStreamBlock[];
  liveDiagnostics: AdminLiveDiagnostics | undefined;
  liveEvents: AdminDiagnostics["recent_activity"];
  teamCount: number;
  keyedTeams: number;
  throughputBps: number;
}) {
  const enabled = !!config?.enabled;
  const reachable = liveDiagnostics?.reachable !== false;
  const onAir = enabled && sessions.length > 0;
  const status = onAir ? "On air" : enabled ? "Ready" : "Disabled";
  const statusTone = onAir ? "bad" : enabled && reachable ? "good" : "warn";

  return (
    <section
      data-testid="admin-live-status"
      data-status={status.toLowerCase().replace(" ", "-")}
      className="rounded border border-rule bg-surface p-3 shadow-sm"
    >
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusDot tone={statusTone} />
            <p className="font-display text-sm uppercase tracking-wider text-ink">
              {status}
            </p>
            <span className="rounded-full border border-rule px-2 py-0.5 font-mono text-[0.65rem] text-ink-muted">
              {sessions.length} viewer{sessions.length === 1 ? "" : "s"}
            </span>
          </div>
          <p className="mt-1 text-sm text-ink-muted">
            {enabled
              ? "Team streams can publish when their key is configured."
              : "Live is disabled; public team pages will show the offline state."}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <SignalTile label="Teams keyed" value={`${keyedTeams}/${teamCount}`} tone={keyedTeams === teamCount && teamCount > 0 ? "good" : "warn"} />
          <SignalTile label="Throughput" value={formatBps(throughputBps)} tone={throughputBps > 0 ? "busy" : "idle"} />
          <SignalTile label="Blocks" value={String(blocks.length)} tone={blocks.length > 0 ? "warn" : "good"} />
          <SignalTile label="Live events" value={String(liveEvents.length)} tone={liveEvents.length > 0 ? "busy" : "idle"} />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// C1 — Ingest config card
// ---------------------------------------------------------------------------

function IngestConfigCard({
  query,
}: {
  query: ReturnType<typeof useQuery<AdminLiveConfig>>;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const config = query.data;
  const [offlineDraft, setOfflineDraft] = useState<string | null>(null);
  const [rtmpDraft, setRtmpDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveSettings = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateAdminSettings(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-live-config"),
      });
      setOfflineDraft(null);
      setRtmpDraft(null);
      toast.success("Ingest config saved");
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't save ingest config", {
        description: formatErr(err),
      });
    },
  });

  return (
    <section
      data-testid="admin-live-ingest"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3 h-fit"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Ingest
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {query.isLoading ? "Loading…" : config?.enabled ? "Enabled" : "Disabled"}
        </span>
      </header>

      {error ? (
        <p
          data-testid="admin-live-ingest-error"
          className="text-destructive font-mono text-xs"
        >
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => {
          setError(null);
          saveSettings.mutate({ live_enabled: config?.enabled ? "0" : "1" });
        }}
        disabled={query.isLoading || saveSettings.isPending}
        data-testid="admin-live-enabled"
        data-active={!!config?.enabled ? "true" : undefined}
        className="self-start rounded border px-3 py-1.5 text-xs font-display uppercase tracking-wider transition-colors border-rule text-ink-muted hover:text-ink hover:bg-field data-[active=true]:border-accent data-[active=true]:bg-accent data-[active=true]:text-accent-ink disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {config?.enabled ? "Live enabled" : "Live disabled"}
      </button>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          RTMP base URL
        </span>
        <input
          type="text"
          value={rtmpDraft ?? config?.rtmp_public_url ?? ""}
          onChange={(e) => setRtmpDraft(e.target.value)}
          placeholder="rtmp://stream.example.com/live"
          data-testid="admin-live-rtmp-url"
          className="rounded border border-rule bg-surface px-2 py-1.5 text-[0.65rem] font-mono text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <p className="text-[0.6rem] text-ink-muted">
          Base URL only — each team&rsquo;s slug is appended automatically
          (e.g. <code>rtmp://stream.example.com/live</code> becomes{" "}
          <code>rtmp://stream.example.com/live/&lt;team-slug&gt;</code>).
          Each team broadcasts with its own stream key, rotated below.
        </p>
        {rtmpDraft !== null ? (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => {
                setError(null);
                saveSettings.mutate({
                  live_rtmp_public_url: rtmpDraft.trim(),
                });
              }}
              disabled={saveSettings.isPending}
              data-testid="admin-live-rtmp-url-save"
              className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setRtmpDraft(null)}
              className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Offline message
        </span>
        <textarea
          value={offlineDraft ?? config?.offline_message ?? ""}
          onChange={(e) => setOfflineDraft(e.target.value)}
          rows={3}
          data-testid="admin-live-offline-message"
          className="rounded border border-rule bg-surface px-2 py-1.5 text-xs text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        {offlineDraft !== null ? (
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => {
                setError(null);
                saveSettings.mutate({ live_offline_message: offlineDraft });
              }}
              disabled={saveSettings.isPending}
              data-testid="admin-live-offline-save"
              className="rounded border border-accent bg-accent text-accent-ink hover:bg-accent-hover px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setOfflineDraft(null)}
              className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-field px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider"
            >
              Cancel
            </button>
          </div>
        ) : null}
      </div>

    </section>
  );
}

// ---------------------------------------------------------------------------
// C2 + C5 — ON-AIR pill + throughput sparkline
// ---------------------------------------------------------------------------

function OnAirAndThroughputRow({
  sessions,
  perf,
  configEnabled,
}: {
  sessions: AdminStreamSession[];
  perf: AdminPerformance | undefined;
  configEnabled: boolean;
}) {
  const onAir =
    configEnabled && sessions.length > 0
      ? "on-air"
      : configEnabled
        ? "standby"
        : "off-air";
  const liveCount = sessions.length;
  const samples = perf?.throughput?.samples ?? [];
  const lastBps = Number(perf?.throughput?.last?.bps_live_out ?? 0);
  const avgBps = Number(perf?.throughput?.avg_live_bps_30s ?? 0);

  return (
    <section
      data-testid="admin-live-onair-row"
      className="rounded border border-rule bg-surface shadow-sm p-3"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <OnAirPill state={onAir} viewers={liveCount} />
        <div className="flex flex-col items-end">
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Live throughput
          </span>
          <span
            data-testid="admin-live-throughput"
            className="text-sm font-mono text-ink"
          >
            {formatBps(lastBps)} <span className="text-ink-muted">· 30s avg {formatBps(avgBps)}</span>
          </span>
        </div>
      </div>
      <ThroughputSparkline samples={samples.map((s) => Number(s.bps_live_out ?? 0))} />
    </section>
  );
}

function OnAirPill({
  state,
  viewers,
}: {
  state: "off-air" | "standby" | "on-air";
  viewers: number;
}) {
  const tone =
    state === "on-air"
      ? "border-destructive/60 text-destructive bg-destructive/10"
      : state === "standby"
        ? "border-accent text-accent bg-accent/10"
        : "border-rule text-ink-muted";
  const label =
    state === "on-air" ? "ON AIR" : state === "standby" ? "STANDBY" : "OFF AIR";
  return (
    <span
      data-testid="admin-live-onair-pill"
      data-state={state}
      className={`inline-flex items-center gap-2 rounded border px-3 py-1 text-xs font-display uppercase tracking-wider ${tone}`}
    >
      <span className="size-1.5 rounded-full bg-current" />
      <span>{label}</span>
      {state === "on-air" ? (
        <span className="font-mono text-[0.65rem]">· {viewers} viewer{viewers === 1 ? "" : "s"}</span>
      ) : null}
    </span>
  );
}

function ThroughputSparkline({ samples }: { samples: number[] }) {
  if (samples.length === 0) {
    return (
      <p className="mt-2 text-[0.6rem] font-mono text-ink-muted">
        No throughput samples yet.
      </p>
    );
  }
  const data = samples.slice(-60);
  const max = Math.max(...data, 1);
  const W = 600;
  const H = 40;
  const step = data.length > 1 ? W / (data.length - 1) : W;
  const points = data
    .map((v, i) => `${(i * step).toFixed(1)},${(H - (v / max) * H).toFixed(1)}`)
    .join(" ");
  return (
    <svg
      data-testid="admin-live-throughput-sparkline"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="mt-2 w-full h-10"
      aria-label="Live throughput last 60 samples"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        points={points}
        className="text-accent"
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// C6 — Encoder load tile
// ---------------------------------------------------------------------------

function EncoderLoadTile({ perf }: { perf: AdminPerformance | undefined }) {
  const host = (perf?.host ?? {}) as Record<string, unknown>;
  const cpu = Number(host.cpu_percent ?? 0);
  const mem = Number(getNested(host, ["memory", "percent"]) ?? 0);
  const gpu = Number(host.intel_gpu_busy_pct ?? 0);
  const txBps = Number(getNested(host, ["net", "bps_tx"]) ?? 0);
  const vodAvgBps = Number(perf?.throughput?.avg_vod_bps_30s ?? 0);
  const concurrency = Number(perf?.transcode?.concurrency_limit ?? 0);

  return (
    <section
      data-testid="admin-live-encoder-tile"
      className="grid grid-cols-2 lg:grid-cols-3 gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <Stat label="CPU" value={`${cpu.toFixed(0)}%`} />
      <Stat label="iGPU" value={`${gpu.toFixed(0)}%`} />
      <Stat label="Memory" value={`${mem.toFixed(0)}%`} />
      <Stat label="NIC TX" value={formatBps(txBps)} />
      <Stat label="VOD avg" value={formatBps(vodAvgBps)} />
      <Stat label="Encoder slots" value={String(concurrency)} />
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-sm font-mono text-ink">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// C3 — Viewers list (with Kill)
// ---------------------------------------------------------------------------

function ViewersList({ sessions }: { sessions: AdminStreamSession[] }) {
  const [killTarget, setKillTarget] = useState<AdminStreamSession | null>(null);
  return (
    <section
      data-testid="admin-live-viewers"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Active viewers
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {sessions.length} live
        </span>
      </header>
      {sessions.length === 0 ? (
        <p
          data-testid="admin-live-viewers-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No live viewers connected.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {sessions.map((s) => (
            <li
              key={s.session_id}
              data-testid="admin-live-viewer-row"
              data-session-id={s.session_id}
              className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1.5"
            >
              <div className="flex flex-col text-[0.6rem] font-mono text-ink-muted min-w-0">
                <span className="text-ink truncate">
                  {s.ip}{" "}
                  {s.geo_country ? (
                    <span className="text-ink-muted">· {s.geo_country}</span>
                  ) : null}
                </span>
                <span>
                  {formatBytesShort(Number(s.bytes_sent ?? 0))} ·{" "}
                  {Math.round(Number(s.duration_seconds ?? 0))}s active ·{" "}
                  {Math.round(Number(s.idle_seconds ?? 0))}s idle
                </span>
              </div>
              <AdminIconButton
                icon={Ban}
                label="Kill viewer session"
                onClick={() => setKillTarget(s)}
                data-testid="admin-live-viewer-kill"
                tone="destructive"
              />
            </li>
          ))}
        </ul>
      )}
      {killTarget ? (
        <KillViewerConfirm
          session={killTarget}
          onClose={() => setKillTarget(null)}
        />
      ) : null}
    </section>
  );
}

function KillViewerConfirm({
  session,
  onClose,
}: {
  session: AdminStreamSession;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const kill = useMutation({
    mutationFn: () => killAdminStream(session.session_id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-streams"),
      });
      toast.success("Session killed");
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't kill session", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Kill this viewer session?"
      message={
        <>
          <p>
            Disconnects <strong>{session.ip}</strong> and short-lists the (ip,
            target) for ~30 s so they can't immediately reconnect.
          </p>
          {error ? (
            <p
              data-testid="admin-live-kill-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={kill.isPending ? "Killing…" : "Kill session"}
      danger
      onConfirm={() => {
        setError(null);
        kill.mutate();
      }}
      onClose={onClose}
      data-testid="admin-live-kill-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// C4 — Blocks list (with Unblock)
// ---------------------------------------------------------------------------

function BlocksList({ blocks }: { blocks: AdminStreamBlock[] }) {
  const [unblockTarget, setUnblockTarget] = useState<AdminStreamBlock | null>(
    null,
  );

  // Always render the section so the testid is stable; the body
  // collapses to an empty-state when there are no blocks.
  return (
    <section
      data-testid="admin-live-blocks"
      className="flex flex-col gap-2 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between">
        <h3 className="text-sm font-display uppercase tracking-wider text-ink">
          Blocked
        </h3>
        <span className="text-[0.6rem] font-mono text-ink-muted">
          {blocks.length} active
        </span>
      </header>
      {blocks.length === 0 ? (
        <p
          data-testid="admin-live-blocks-empty"
          className="text-xs font-mono text-ink-muted"
        >
          No active blocks.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {blocks.map((b, i) => (
            <li
              key={`${b.ip}-${b.kind}-${i}`}
              data-testid="admin-live-block-row"
              className="grid grid-cols-[1fr_auto] gap-2 items-center rounded border border-rule/60 bg-elevated px-2 py-1.5"
            >
              <div className="flex flex-col text-[0.6rem] font-mono text-ink-muted min-w-0">
                <span className="text-ink truncate">
                  {b.ip} <span className="text-ink-muted">· {b.kind}</span>
                </span>
                <span>
                  {b.match_id ? `match ${b.match_id}` : "live stream"}
                  {b.slot ? ` · ${b.slot}` : ""}
                  {b.remaining_seconds !== undefined
                    ? ` · expires in ${Math.round(b.remaining_seconds)}s`
                    : ""}
                </span>
              </div>
              <AdminIconButton
                icon={ShieldOff}
                label="Unblock stream entry"
                onClick={() => setUnblockTarget(b)}
                data-testid="admin-live-block-unblock"
              />
            </li>
          ))}
        </ul>
      )}
      {unblockTarget ? (
        <UnblockConfirm
          block={unblockTarget}
          onClose={() => setUnblockTarget(null)}
        />
      ) : null}
    </section>
  );
}

function UnblockConfirm({
  block,
  onClose,
}: {
  block: AdminStreamBlock;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const unblock = useMutation({
    mutationFn: () =>
      unblockAdminStream({
        ip: block.ip,
        kind: (block.kind || "live") as "live" | "vod-hls" | "vod-mp4",
        match_id: block.match_id ?? null,
        slot: block.slot ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-streams"),
      });
      toast.success("IP unblocked");
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't unblock IP", { description: formatErr(err) });
    },
  });
  return (
    <ConfirmDialog
      title="Unblock this entry?"
      message={
        <>
          <p>
            Allows <strong>{block.ip}</strong> to reconnect to{" "}
            <strong>{block.match_id ? `${block.match_id}/${block.slot ?? "live"}` : "live"}</strong>{" "}
            immediately.
          </p>
          {error ? (
            <p
              data-testid="admin-live-unblock-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={unblock.isPending ? "Unblocking…" : "Unblock"}
      onConfirm={() => {
        setError(null);
        unblock.mutate();
      }}
      onClose={onClose}
      data-testid="admin-live-unblock-confirm"
    />
  );
}

// ---------------------------------------------------------------------------
// C8 — Live diagnostics block
// ---------------------------------------------------------------------------

function LiveDiagnosticsBlock({
  diagnostics,
  liveDiagnostics,
  liveDiagnosticsFetching,
  liveDiagnosticsError,
  onRefresh,
}: {
  diagnostics: AdminDiagnostics | undefined;
  liveDiagnostics: AdminLiveDiagnostics | undefined;
  liveDiagnosticsFetching: boolean;
  liveDiagnosticsError: unknown;
  onRefresh: () => void;
}) {
  const liveEvents = useMemo(
    () => liveActivityEvents(diagnostics),
    [diagnostics],
  );
  const publisher = liveDiagnostics?.publisher;
  const publisherReady = Boolean(publisher?.ready);
  const connections = liveDiagnostics?.rtmp_connections ?? [];
  const rejections = (liveDiagnostics?.recent_rejections ?? []).slice(0, 5);
  const otherPaths = (liveDiagnostics?.paths ?? []).filter(
    (path) => String(path.name ?? "") !== String(liveDiagnostics?.stream_path ?? ""),
  );

  return (
    <section
      data-testid="admin-live-diagnostics"
      className="flex flex-col gap-3 rounded border border-rule bg-surface shadow-sm p-3"
    >
      <header className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Diagnostics
          </h3>
          <p className="text-[0.65rem] font-mono text-ink-muted">
            MediaMTX ingest health + live stream activity
          </p>
        </div>
        <AdminIconButton
          icon={RefreshCw}
          label={liveDiagnosticsFetching ? "Checking diagnostics" : "Run diagnostics"}
          onClick={onRefresh}
          disabled={liveDiagnosticsFetching}
          data-testid="admin-live-diagnostics-refresh"
          size="md"
        />
      </header>

      {liveDiagnosticsError ? (
        <p
          data-testid="admin-live-diagnostics-error"
          className="text-destructive font-mono text-xs"
        >
          {formatErr(liveDiagnosticsError)}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <DiagnosticPill
          testid="admin-live-mediamtx-pill"
          tone={liveDiagnostics?.reachable ? "good" : "bad"}
          label={liveDiagnostics?.reachable ? "MediaMTX reachable" : "MediaMTX unreachable"}
        />
        <DiagnosticPill
          testid="admin-live-publisher-pill"
          tone={publisherReady ? "good" : "warn"}
          label={
            publisher
              ? `Publisher ${publisherReady ? "ready" : "not ready"}${publisher.bytes_received != null ? ` · ${formatBytesShort(Number(publisher.bytes_received))} in` : ""}`
              : "No publisher on configured path"
          }
        />
        <DiagnosticPill
          testid="admin-live-stream-path-pill"
          tone="muted"
          label={`Path ${redactStreamPath(liveDiagnostics?.stream_path)}`}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-2">
        <DiagnosticList title="RTMP connections" count={connections.length} testid="admin-live-rtmp-connections">
          {connections.length === 0 ? (
            <DiagEmpty copy="No active RTMP connections." />
          ) : (
            connections.map((conn, index) => (
              <DiagnosticRow key={`${String(conn.remote_addr ?? "?")}-${index}`} testid="admin-live-rtmp-row">
                <code>{String(conn.remote_addr ?? "?")}</code>
                <span>state={String(conn.state ?? "unknown")}</span>
                <span>path={redactStreamPath(conn.path)}</span>
                <span>in={formatBytesShort(Number(conn.bytes_received ?? 0))}</span>
                <span>out={formatBytesShort(Number(conn.bytes_sent ?? 0))}</span>
              </DiagnosticRow>
            ))
          )}
        </DiagnosticList>

        <DiagnosticList title="Recent auth rejections" count={rejections.length} testid="admin-live-auth-rejections">
          {rejections.length === 0 ? (
            <DiagEmpty copy="No recent auth rejections." />
          ) : (
            rejections.map((rej, index) => (
              <DiagnosticRow key={`${String(rej.ts ?? "")}-${index}`} testid="admin-live-auth-rejection-row">
                <code>{String(rej.ts ?? "")}</code>
                <span>ip={String(rej.ip ?? "?")}</span>
                <span>{String(rej.action ?? "?")}/{String(rej.protocol ?? "?")}</span>
                <span>path={redactStreamPath(rej.path)}</span>
                <strong>{String(rej.reason ?? "Rejected")}</strong>
              </DiagnosticRow>
            ))
          )}
        </DiagnosticList>
      </div>

      {otherPaths.length > 0 ? (
        <DiagnosticList title="Other MediaMTX paths" count={otherPaths.length} testid="admin-live-other-paths">
          {otherPaths.map((path, index) => (
            <DiagnosticRow key={`${String(path.name ?? "")}-${index}`} testid="admin-live-path-row">
              <code>{redactStreamPath(path.name)}</code>
              <span>ready={path.ready ? "yes" : "no"}</span>
            </DiagnosticRow>
          ))}
        </DiagnosticList>
      ) : null}

      <DiagnosticList title="Live activity" count={liveEvents.length} testid="admin-live-activity">
        {liveEvents.length === 0 ? (
          <DiagEmpty copy="No live stream activity in the recent feed." />
        ) : (
          liveEvents.map((event, index) => (
            <DiagnosticRow
              key={`${event.timestamp}-${event.event_type}-${index}`}
              testid="admin-live-activity-row"
              severity={event.severity}
            >
              <span>{formatTime(event.timestamp)}</span>
              <span>{event.event_type}</span>
              <span>{event.message || "Live event"}</span>
              {event.actor ? <span>by {event.actor}</span> : null}
            </DiagnosticRow>
          ))
        )}
      </DiagnosticList>

      <p className="rounded border border-rule/60 bg-elevated px-2 py-1.5 text-[0.6rem] font-mono text-ink-muted">
        Hint: RTMP state <code>read</code> means the handshake completed; <code>idle</code> or <code>preRead</code> means the camera opened the socket but has not published yet.
      </p>
    </section>
  );
}

function DiagnosticPill({
  testid,
  tone,
  label,
}: {
  testid: string;
  tone: "good" | "warn" | "bad" | "muted";
  label: string;
}) {
  const classes = {
    good: "border-good/50 text-good bg-good/10",
    warn: "border-accent/50 text-accent bg-accent/10",
    bad: "border-destructive/50 text-destructive bg-destructive/10",
    muted: "border-rule text-ink-muted bg-elevated",
  }[tone];
  return (
    <span
      data-testid={testid}
      data-tone={tone}
      className={`rounded border px-2 py-1 text-[0.6rem] font-display uppercase tracking-wider ${classes}`}
    >
      {label}
    </span>
  );
}

function DiagnosticList({
  title,
  count,
  testid,
  children,
}: {
  title: string;
  count: number;
  testid: string;
  children: ReactNode;
}) {
  return (
    <div data-testid={testid} className="flex flex-col gap-1.5 rounded border border-rule/60 bg-elevated p-2">
      <div className="flex items-baseline justify-between">
        <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {title}
        </h4>
        <span className="text-[0.6rem] font-mono text-ink-muted">{count}</span>
      </div>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

function DiagnosticRow({
  testid,
  severity,
  children,
}: {
  testid: string;
  severity?: string;
  children: ReactNode;
}) {
  const tone = severity === "warning" ? "border-accent/50" : severity === "error" ? "border-destructive/50" : "border-rule/60";
  return (
    <div
      data-testid={testid}
      data-severity={severity ?? "info"}
      className={`flex flex-wrap gap-x-2 gap-y-1 rounded border ${tone} bg-surface px-2 py-1.5 text-[0.6rem] font-mono text-ink-muted`}
    >
      {children}
    </div>
  );
}

function DiagEmpty({ copy }: { copy: string }) {
  return <p className="text-[0.6rem] font-mono text-ink-muted">{copy}</p>;
}

// ---------------------------------------------------------------------------
// helpers
// ---------------------------------------------------------------------------

type SignalTone = "good" | "warn" | "bad" | "busy" | "idle" | "muted";

function SignalTile({
  label,
  value,
  tone,
}: {
  label: string;
  value: ReactNode;
  tone: SignalTone;
}) {
  const classes: Record<SignalTone, string> = {
    good: "text-good",
    warn: "text-accent",
    bad: "text-destructive",
    busy: "text-accent",
    idle: "text-ink-muted",
    muted: "text-ink",
  };
  return (
    <div className="rounded border border-rule bg-elevated px-2 py-1.5">
      <span className="block text-[0.55rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className={`font-mono text-xs ${classes[tone]}`}>{value}</span>
    </div>
  );
}

function StatusDot({ tone }: { tone: SignalTone }) {
  const classes: Record<SignalTone, string> = {
    good: "bg-good",
    warn: "bg-accent",
    bad: "bg-destructive",
    busy: "bg-accent",
    idle: "bg-ink-muted",
    muted: "bg-ink-muted",
  };
  return (
    <span
      aria-hidden="true"
      className={`mt-1 h-2 w-2 shrink-0 rounded-full ${classes[tone]}`}
    />
  );
}

function liveActivityEvents(
  diagnostics: AdminDiagnostics | undefined,
): AdminDiagnostics["recent_activity"] {
  return (diagnostics?.recent_activity ?? [])
    .filter((event) => {
      const kind = String(event.metadata?.kind ?? "").toLowerCase();
      const type = event.event_type.toLowerCase();
      return (
        kind === "live" ||
        type.startsWith("live.") ||
        type === "stream.killed" ||
        (type.startsWith("stream.") && kind === "live")
      );
    })
    .slice(0, 8);
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

function formatBps(bps: number): string {
  if (!Number.isFinite(bps) || bps <= 0) return "0 bps";
  const units = ["bps", "Kbps", "Mbps", "Gbps"];
  let v = bps;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatBytesShort(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

function formatTime(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function redactStreamPath(value: unknown): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "—";
  const parts = raw.split("/").filter(Boolean);
  if (parts.length === 0) return "—";
  if (parts.length === 1) return "••••";
  return `${parts[0]}/••••`;
}

function getNested(obj: Record<string, unknown>, path: string[]): unknown {
  let cur: unknown = obj;
  for (const k of path) {
    if (cur && typeof cur === "object" && k in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[k];
    } else {
      return undefined;
    }
  }
  return cur;
}

// ---------------------------------------------------------------------------
// M12c — per-team team picker + rotate
// ---------------------------------------------------------------------------

/**
 * Compact roster of teams with their live-stream affordances. The picker
 * is a flat list (single-club app — there aren't enough teams to warrant
 * a search UI); each row exposes a Rotate-key action that goes through
 * ConfirmDialog.
 */
function AdminTeamsLiveStrip({
  query: teamsQuery,
}: {
  query: ReturnType<typeof useQuery<AdminTeamRow[]>>;
}) {
  return (
    <section
      data-testid="admin-live-teams"
      className="rounded border border-rule bg-surface p-3 shadow-sm"
    >
      <header className="flex items-center justify-between gap-2 pb-2">
        <div>
          <h3 className="text-sm font-display uppercase tracking-wider text-ink">
            Team Broadcast Readiness
          </h3>
          <p className="text-[0.65rem] text-ink-muted">
            Stream paths and key rotation for each team.
          </p>
        </div>
        <span className="text-[0.65rem] font-mono uppercase tracking-wide text-ink-muted">
          {teamsQuery.data?.length ?? 0} teams
        </span>
      </header>
      {teamsQuery.isLoading ? (
        <p className="text-ink-muted text-xs font-mono">Loading teams…</p>
      ) : teamsQuery.isError ? (
        <p className="text-destructive text-xs font-mono">
          Couldn’t load teams.
        </p>
      ) : (teamsQuery.data ?? []).length === 0 ? (
        <p className="text-ink-muted text-xs font-mono">
          No teams yet — create one in /admin/teams.
        </p>
      ) : (
        <ul role="list" className="flex flex-col gap-1.5">
          {teamsQuery.data!.map((team) => (
            <li key={team.id}>
              <AdminTeamLiveRow team={team} />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function AdminTeamLiveRow({ team }: { team: AdminTeamRow }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  // Composite paste-ready Stream Key (``<slug>/<key>``) — the value
  // the operator pastes into OBS verbatim. We keep the composite
  // form in state (not just the bare key) because the documented
  // RTMP Server is the base URL alone, so the bare key would not
  // work on its own.
  const [rotated, setRotated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Per-row audit lookup so the confirm dialog can warn the admin
  // specifically — "you'd be locking out <coach> who rotated 2 hours
  // ago" — instead of a generic "this might disrupt someone."
  //
  // Gated on `open` so the strip doesn't fan out N audit fetches on
  // page mount: a club with 20 teams would otherwise queue 20
  // parallel queries before the admin has even decided which team
  // to touch. The audit data is most useful at the moment of
  // action — the dialog — so deferring the lookup until then loses
  // no UX and saves N − k requests (where k is the number of
  // rotate clicks per page visit, typically 0 or 1).
  const auditQuery = useQuery({
    queryKey: unscopedKey("team-stream-key-audit", { teamId: team.id }),
    queryFn: () => getTeamStreamKeyAudit(team.id),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled: open,
  });

  const onRotate = async () => {
    setPending(true);
    setError(null);
    try {
      const resp = await rotateTeamStreamKey(team.id);
      // M12c-fix: the usable Stream Key is the composite
      // ``<slug>/<key>`` (paired with the base RTMP Server URL).
      // Prefer ``resp.stream_path`` so we stay in sync with what
      // the server says the canonical publish path is, and strip
      // the leading ``live/`` namespace so the value is what an
      // operator pastes verbatim into OBS's Stream Key field.
      const composite = resp.stream_path.replace(/^live\//, "");
      setRotated(composite);
      // Refresh the team roster so the "no key" → "keyed" badge updates
      // without a manual reload.
      await queryClient.invalidateQueries({
        queryKey: unscopedKey("admin-teams-live"),
      });
      // Push the fresh audit metadata into cache so the notice
      // updates without a refetch round-trip.
      queryClient.setQueryData(
        unscopedKey("team-stream-key-audit", { teamId: team.id }),
        {
          last_rotated_at: resp.last_rotated_at,
          last_rotated_by: resp.last_rotated_by,
        },
      );
      setOpen(false);
      toast.success("Stream key rotated");
    } catch (err) {
      setError(
        err instanceof ApiError && typeof err.detail === "string"
          ? err.detail
          : "Couldn't rotate the team stream key.",
      );
      toast.error("Couldn't rotate stream key", {
        description:
          err instanceof ApiError && typeof err.detail === "string"
            ? err.detail
            : "Couldn't rotate the team stream key.",
      });
    } finally {
      setPending(false);
    }
  };

  return (
    <article
      data-testid="admin-live-team-row"
      data-team-slug={team.slug}
      className="flex flex-wrap items-center justify-between gap-2 rounded border border-rule bg-surface px-3 py-2"
    >
      <div className="flex min-w-0 flex-col gap-0.5">
        <p className="font-display text-sm text-ink truncate">{team.name}</p>
        <p className="font-mono text-[0.6rem] uppercase tracking-mono text-ink-muted">
          live/{team.slug}
          {team.public ? " · public" : " · private"}
          {team.has_stream_key ? " · keyed" : " · no key"}
        </p>
        {rotated ? (
          <p
            data-testid="admin-live-team-rotated"
            className="font-mono text-[0.65rem] text-ink"
          >
            New Stream Key (paste into OBS):{" "}
            <code className="rounded bg-canvas px-1 py-0.5 text-ink">
              {rotated}
            </code>
          </p>
        ) : null}
        {error ? (
          <p className="font-mono text-[0.65rem] text-destructive">{error}</p>
        ) : null}
      </div>
      <AdminIconButton
        icon={KeyRound}
        label="Rotate stream key"
        onClick={() => setOpen(true)}
        data-testid="admin-live-team-rotate"
        tone="destructive"
        size="md"
      />
      {open ? (
        <ConfirmDialog
          title={`Rotate ${team.name}'s stream key?`}
          message={
            <div className="flex flex-col gap-2">
              <p>
                This generates a new key and{" "}
                <strong>immediately disconnects</strong> any encoder still
                streaming with the previous key. The new key is shown{" "}
                <strong>once</strong> in this dialog — copy it before
                closing.
              </p>
              {auditQuery.data?.last_rotated_by ? (
                <p className="rounded border border-destructive/40 bg-destructive/10 p-2 text-xs">
                  <strong>{auditQuery.data.last_rotated_by}</strong>{" "}
                  rotated this key at{" "}
                  <code>{auditQuery.data.last_rotated_at}</code>. If
                  they&rsquo;re still using it, rotating now will lock
                  them out — confirm with the team admin before
                  proceeding.
                </p>
              ) : (
                <p className="text-xs text-ink-muted">
                  This team&rsquo;s key has not been rotated before — no
                  audit trail to check.
                </p>
              )}
            </div>
          }
          confirmLabel={pending ? "Rotating…" : "Rotate key"}
          danger
          onConfirm={() => void onRotate()}
          onClose={() => {
            if (pending) return;
            setOpen(false);
            setError(null);
          }}
          data-testid="admin-live-team-rotate-confirm"
        />
      ) : null}
    </article>
  );
}
